import { useState, useRef, useEffect, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, FormLabel, OutlinedInput, Box, Radio, Chip, RadioGroup, FormControlLabel } from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline, FlightTakeoff, FlightLand } from '@mui/icons-material';

import TextField from '@Components/Shared/Controls/TextField'
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import atoms from '@Components/Utilities/SandhillsAtoms';
import DateRangePicker from '@Components/MuiWrappers/DateRangePicker';
import QuoteConfirmationModal from '@Components/Shared/Modals/QuoteConfirmationModal ';
import { sandhillsRequest, focusErroredField } from '@Components/Utilities/SandhillsUtility';
import { CharterFlightType } from '@Components/Utilities/SandhillsConstants';

const RequestACharter = (props) => {
    const routes = useRecoilValue(atoms.routes);
    const inputReferences = useRef({});
    const timeout = useRef(null);
    const [success, setSuccess] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [showConfirmation, setShowConfirmation] = useState(false);
    const currentUser = useRecoilValue(atoms.currentUser);
    const toggleConfirmationModal = () => {
        setShowConfirmation(showConfirmation => !showConfirmation);
    }
    const [shouldRedirect, setShouldRedirect] = useState(props.TryRedirectSave);
    const [userError, setUserError] = useState(false);
    const [userErrorMessage, setUserErrorMessage] = useState('');
    const [responseError, setResponseError] = useState(props.ErrorMessage || '');
    const [isTestOverride, setIsTestOverride] = useState(props.IsTestOverride || false);
    const aircraftTypes = ['Jet', 'Turboprop', 'Piston', 'Helicopter'];

    const [formData, setFormData] = useState({
        SelectedAircraftPrefType: props.SelectedAircraftPrefType ? props.SelectedAircraftPrefType : [],
        TypeOfFlight: props.TypeOfFlight ? props.TypeOfFlight : CharterFlightType.RoundTrip,
        DepartingDate: props.DepartingDate ? new Date(props.DepartingDate) : null,
        ReturningDate: props.ReturningDate ? new Date(props.ReturningDate) : null,
        NumberOfPassenger: props.NumberOfPassenger || '',
        FromCityAirportCode: props.FromCityAirportCode || null,
        ToCityAirportCode: props.ToCityAirportCode || null,
        Comments: props.Comments || ''

    });
    useEffect(() => {
        if (shouldRedirect) {
            onSubmit();
            setShouldRedirect(false);
        }
    }, []);

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    const submitForm = (e) => {
        e.preventDefault();
        let errors = run(formData, [
            ruleRunner('SelectedAircraftPrefType', translator.translate('sandhills.SelectedAircraftPrefType'), ValidationRules.hasValue),
            ruleRunner('TypeOfFlight', translator.translate('sandhills.typeofflight')),
            ruleRunner('DepartingDate', translator.translate('sandhills.datefrom'), ValidationRules.required),
            ruleRunner('ReturningDate', translator.translate('sandhills.dateto')),
            ruleRunner('NumberOfPassenger', translator.translate('sandhills.NumberOfPassenger'), ValidationRules.required, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(1, false)),
            ruleRunner('FromCityAirportCode', translator.translate('sandhills.FromCityAirportCode'), ValidationRules.required),
            ruleRunner('ToCityAirportCode', translator.translate('sandhills.ToCityAirportCode'), ValidationRules.required)
        ]);

        setValidationErrors(errors);
        focusErroredField(errors, inputReferences);
        setSubmitted(false);

        if (Object.keys(errors).length <= 0) {
            setSuccess(false);
            setDisableSubmit(true);
            if (!currentUser.IsAuthenticated) {
                window.location.href = routes.SSO + '?to=' + encodeURIComponent(window.location.href);
            }
            else {
                onSubmit();
            }
        }
    };

    const onSubmit = (token) => {
        sandhillsRequest({
            url: routes.CharterRequestSave,
            method: 'post',
            data: {
                SelectedAircraftPrefType: formData.SelectedAircraftPrefType,
                TypeOfFlight: formData.TypeOfFlight,
                EquipmentMessageStatus: props.EquipmentMessageStatus,
                DepartingDate: formData.DepartingDate,
                ReturningDate: formData.ReturningDate,
                FromCityAirportCode: formData.FromCityAirportCode,
                ToCityAirportCode: formData.ToCityAirportCode,
                NumberOfPassenger: formData.NumberOfPassenger,
                Comments: formData.Comments,
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            if (data.Success) {
                setIsTestOverride(data?.IsTestOverride);
                setShowConfirmation(true);
                setResponseError(null);
                setFormData(previousState => {
                    return {
                        ...previousState,
                        SelectedAircraftPrefType: [],
                        TypeOfFlight: CharterFlightType.RoundTrip,
                        DepartingDate: null,
                        ReturningDate: null,
                        FromCityAirportCode: null,
                        ToCityAirportCode: null,
                        NumberOfPassenger: 1,
                        Comments: ''
                    };
                });
            } else {
                setResponseError(data?.ErrorMessage);
                if (props.IsEmployee && !isTestOverride) {
                    setUserError(true);
                    setUserErrorMessage(<p className='error'>{translator.translate('sandhills.quoterequestemployee')}</p>);
                    setShowConfirmation(true);
                }
                else if (!currentUser.IsAuthenticated && !props.IsEmployee && !isTestOverride) {
                    setUserError(true);
                    setUserErrorMessage(<p className='error'>{translator.translate('sandhills.quoterequestlogin')}</p>);
                    setShowConfirmation(true);
                }
            }

            setSuccess(data.Success);
            setSubmitted(true);
            setDisableSubmit(false);
            setValidationErrors(data.ValidationErrors || {});
            focusErroredField(data.ValidationErrors, inputReferences);
        });
    };

    const closeModalTimer = () => {
        setShowConfirmation(false);
    };

    const getAutoCompleteUrl = (e, url) => {
        return url;
    };

    const getAutoCompleteData = (e) => {
        return {
            text: e.target.value
        };
    };

    const setSelectedAircraftPrefType = (event) => {
        let value = event.target.value;

        if (value.includes('Select All')) {
            if (formData.SelectedAircraftPrefType.length == aircraftTypes.length) {
                // Remove all
                value = [];
            }
            else {
                value = aircraftTypes;
            }
        }
        setFormData(previousState => {
            // On autofill we get a stringified value.
            return {
                ...previousState, SelectedAircraftPrefType: value
            };
        });
    };

    const onChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
    };

    const onAirportChange = (e) => {
        let id = e.target.id;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [id]: { AirportCodeDisplay: value } };
        });
    }

    const handleDecrement = () => {
        if (formData.NumberOfPassenger <= 1) {
            return;
        }

        setFormData(previousState => {
            return { ...previousState, NumberOfPassenger: previousState.NumberOfPassenger - 1 };
        });
    }

    const handleIncrement = () => {
        setFormData(previousState => {
            return { ...previousState, NumberOfPassenger: previousState.NumberOfPassenger + 1 };
        });
    }

    const setDateRanges = (startDate, endDate) => {
        setFormData(previousState => {
            return { ...previousState, DepartingDate: startDate, ReturningDate: endDate };
        });
    }

    const setFlightType = (e) => {
        setFormData(previousState => {
            return { ...previousState, TypeOfFlight: e.target.value };
        });
    }

    return (
        <div className='request-a-charter hero-image'>
            {showConfirmation &&
                <QuoteConfirmationModal
                    onClose={closeModalTimer}
                    toggleConfirmationModal={toggleConfirmationModal}
                    title={isTestOverride ? "Request A Charter - Employee Test" : translator.translate('sandhills.tradesites.mvc.requestaprivatecharterflightquote')}
                    message={isTestOverride ? "Test Charter Quote Email Lead Was Succcessfully Sent" : userError ? userErrorMessage : translator.translate('sandhills.quoterequestsent')} />
            }
            <div className='container'>
                <FormControl id='raqform' className='form'>
                    <h1>{<TranslationText tKey='sandhills.tradesites.mvc.requestaprivatecharterflightquote' />}</h1>
                    {responseError && <p className='error'>{responseError}</p>}
                    <div className='form-row'>
                        <div className='button-input aircraft-selector'>
                            <FormLabel component="legend">{<TranslationText tKey='sandhills.typeofflight' />}</FormLabel>
                            <RadioGroup aria-label="position" name="aircraft-selector-radio-group" value={formData.TypeOfFlight} onChange={setFlightType} row>
                                <FormControlLabel
                                    value={CharterFlightType.RoundTrip}
                                    control={<Radio />}
                                    label={<TranslationText tKey='sandhills.roundtrip' />}
                                    labelPlacement="end"
                                />
                                <FormControlLabel
                                    value={CharterFlightType.OneWay}
                                    control={<Radio />}
                                    label={<TranslationText tKey='sandhills.oneway' />}
                                    labelPlacement="end"
                                />
                            </RadioGroup>
                        </div>
                        <div className='button-input passenger-selector'>
                            <FormLabel component="legend">{<TranslationText tKey='sandhills.passengers' />}</FormLabel>
                            <Box className='number-counter'>
                                <RemoveCircleOutline onClick={handleDecrement} className={`${formData.NumberOfPassenger <= 1 ? 'disabled' : ''}`} />
                                <strong>{formData.NumberOfPassenger}</strong>
                                <AddCircleOutline onClick={handleIncrement} />
                            </Box>
                        </div>
                    </div>
                    <div className='form-row'>
                        <div className='RAC-input-container airport-selector'>
                            <div className='boxed-input'>
                                <FlightTakeoff className='material-icon' />
                                <TextField
                                    id='FromCityAirportCode'
                                    name='FromCityAirportCode'
                                    label={<TranslationText tKey='sandhills.tradesites.mvc.cityairportfrom' />}
                                    value={formData.FromCityAirportCode?.AirportCodeDisplay}
                                    type='text'
                                    required={true}
                                    ref={(element) => inputReferences.current[`FromCityAirportCode`] = element}
                                    updateField={(value, airportcodeid, cityid) => setFormData(previousState => {
                                        return { ...previousState, FromCityAirportCode: { AirportCodeID: airportcodeid, AirportCityID: cityid, AirportCodeDisplay: value } };
                                    })}
                                    onFieldChanged={onAirportChange}
                                    autoComplete={{
                                        ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetCityAirportCodeAutoComplete),
                                        postData: (e) => getAutoCompleteData(e)
                                    }}
                                    useMUI
                                    InputProps={{
                                        disableUnderline: true
                                    }}
                                />
                            </div>
                            <span className='error'>{validationErrors.FromCityAirportCode}</span>
                        </div>
                        <div className='button-input icon-col'>
                            <i className='fa-solid fa-arrow-right-arrow-left'></i>
                        </div>
                        <div className='RAC-input-container airport-selector'>
                            <div className='boxed-input'>
                                <FlightLand className='material-icon' />
                                <TextField
                                    id='ToCityAirportCode'
                                    labelId='ToCityAirportCode'
                                    name='ToCityAirportCode'
                                    label={<TranslationText tKey='sandhills.tradesites.mvc.cityairportto' />}
                                    value={formData.ToCityAirportCode?.AirportCodeDisplay}
                                    type='text'
                                    required={true}
                                    ref={(element) => inputReferences.current[`ToCityAirportCode`] = element}
                                    onFieldChanged={onAirportChange}
                                    updateField={(value, airportcodeid, cityid) => setFormData(previousState => {
                                        return { ...previousState, ToCityAirportCode: { AirportCodeID: airportcodeid, AirportCityID: cityid, AirportCodeDisplay: value } };
                                    })}
                                    autoComplete={{
                                        ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetCityAirportCodeAutoComplete),
                                        postData: (e) => getAutoCompleteData(e)
                                    }}
                                    useMUI
                                    InputProps={{
                                        disableUnderline: true
                                    }}
                                />
                            </div>
                            <span className='error'>{validationErrors.ToCityAirportCode}</span>
                        </div>
                        <div className='RAC-input-container date-range-picker'>
                            <div className='boxed-input'>
                                <DateRangePicker departingDate={formData.DepartingDate} returningDate={formData.ReturningDate} setDateRanges={setDateRanges} isRoundTrip={formData.TypeOfFlight == CharterFlightType.RoundTrip} validationErrors={validationErrors.DepartingDate} />
                            </div>
                            <span className='error'>{validationErrors.DepartingDate}</span>
                        </div>
                    </div>
                    <div className='form-row'>
                        <div className='RAC-input-container aircraft-selector'>
                            <div className='boxed-input'>
                                <FormControl fullWidth variant='outlined'>
                                    <InputLabel
                                        className='shrink-label'
                                        htmlFor="TypeofAircraft-label"
                                        aria-label={translator.translate('sandhills.typeofaircraft')}
                                    >
                                        {translator.translate('sandhills.typeofaircraft')}
                                    </InputLabel>
                                    <Select
                                        labelId="TypeofAircraft-label"
                                        id="TypeofAircraft"
                                        multiple
                                        label={translator.translate('sandhills.typeofaircraft')}
                                        value={formData.SelectedAircraftPrefType}
                                        onChange={setSelectedAircraftPrefType}
                                        input={<OutlinedInput label="Type of Aircraft"
                                            inputProps={{
                                                id: "TypeofAircraft-label"
                                            }} />}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {selected.map((value) => (
                                                    <Chip key={value} label={value} size='small' />
                                                ))}
                                            </Box>
                                        )}
                                        MenuProps={MenuProps}
                                    >
                                        <MenuItem value='Select All'>
                                            <Checkbox checked={formData.SelectedAircraftPrefType.length === aircraftTypes.length} />
                                            <ListItemText primary='Select All' />
                                        </MenuItem>
                                        {aircraftTypes.map((name) => (
                                            <MenuItem key={name} value={name}>
                                                <Checkbox checked={formData.SelectedAircraftPrefType.indexOf(name) > -1} />
                                                <ListItemText primary={name} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                            <span className='error'>{validationErrors.SelectedAircraftPrefType}</span>
                        </div>
                        <div className='RAC-input-container'>
                            <TextField
                                id='Comments'
                                name='Comments'
                                value={formData.Comments}
                                type='text'
                                class='boxed-input comments'
                                label={<TranslationText tKey='sandhills.tradepubwebSite.mvc.comments' />}
                                inputClass='text-area'
                                required={false}
                                ref={(element) => inputReferences.current[`Comments`] = element}
                                onFieldChanged={onChange}
                                useMUI
                                multiline
                                fullWidth
                                aria-label={translator.translate('sandhills.tradepubwebSite.mvc.comments')}
                                InputProps={{
                                    disableUnderline: true
                                }}
                            />
                            <span className='error'>{validationErrors.Comments}</span>
                        </div>
                        <input className='ts-button submit-button button' type='submit' id='request-a-charter-submit-button' name='request-a-charter-submit-button' value={translator.translate('sandhills.tradepubwebsite.mvc.submit')} disabled={disableSubmit} onClick={submitForm} />
                    </div>
                    {disableSubmit && <LoadingRing backdrop />}
                </FormControl>
            </div >
        </div>
    );
};

export default RequestACharter;